import { useEffect, useRef, useState } from 'react';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import {
  deleteStaffDuitNowQr,
  fetchStaffDuitNowQrBlob,
  uploadStaffDuitNowQr,
} from '@/api/staff';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;

function validateImageFile(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'Please upload a PNG, JPG, or WEBP image';
  }
  if (file.size > MAX_BYTES) {
    return 'Image must be 2 MB or smaller';
  }
  return null;
}

type DuitNowQrUploadProps = {
  staffId: string;
  hasDuitNowQr: boolean;
};

function DuitNowQrUpload({ staffId, hasDuitNowQr }: DuitNowQrUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [hasImage, setHasImage] = useState(hasDuitNowQr);
  const [loadingPreview, setLoadingPreview] = useState(hasDuitNowQr);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const ignoreFetchRef = useRef(false);

  useEffect(() => {
    if (!hasDuitNowQr) {
      setLoadingPreview(false);
      return;
    }

    let cancelled = false;
    setLoadingPreview(true);
    void fetchStaffDuitNowQrBlob(staffId)
      .then((blob) => {
        if (cancelled || ignoreFetchRef.current) return;
        setPreviewUrl((current) => {
          if (current) URL.revokeObjectURL(current);
          return URL.createObjectURL(blob);
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load DuitNow QR');
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [staffId, hasDuitNowQr]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFile = async (file: File | undefined) => {
    if (!file || uploading || removing) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      setSuccess('');
      return;
    }

    setError('');
    setSuccess('');
    setUploading(true);
    try {
      await uploadStaffDuitNowQr(staffId, file);
      ignoreFetchRef.current = true;
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return objectUrl;
      });
      setHasImage(true);
      setSuccess('DuitNow QR uploaded');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload DuitNow QR');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setError('');
    setSuccess('');
    setRemoving(true);
    try {
      await deleteStaffDuitNowQr(staffId);
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      setHasImage(false);
      setSuccess('DuitNow QR removed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove DuitNow QR');
    } finally {
      setRemoving(false);
    }
  };

  const busy = uploading || removing;

  return (
    <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Lucide icon="QrCode" className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          DuitNow QR Upload
        </h3>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
        Upload this ambassador&apos;s DuitNow QR. PNG, JPG, or WEBP, up to 2 MB.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />

      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!busy) setDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!busy) setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
          void handleFile(e.dataTransfer.files?.[0]);
        }}
        className={`flex w-full min-h-[220px] flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition ${
          dragging
            ? 'border-primary bg-primary/5'
            : 'border-slate-200 dark:border-darkmode-400 bg-slate-50/80 dark:bg-darkmode-600/40'
        } ${busy ? 'cursor-wait opacity-70' : 'cursor-pointer hover:border-primary/60'}`}
      >
        {loadingPreview ? (
          <>
            <Lucide icon="Loader" className="w-8 h-8 animate-spin text-slate-400" />
            <p className="mt-3 text-sm text-slate-500">Loading QR…</p>
          </>
        ) : previewUrl ? (
          <img
            src={previewUrl}
            alt="DuitNow QR"
            className="max-h-48 max-w-full rounded-lg bg-white object-contain p-2 shadow-sm"
          />
        ) : (
          <>
            <Lucide icon="Upload" className="w-8 h-8 text-slate-400" />
            <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
              {uploading ? 'Uploading…' : 'Select a file or drag and drop'}
            </p>
            <p className="mt-1 text-xs text-slate-400">DuitNow QR image</p>
          </>
        )}
      </button>

      {error ? (
        <div className="mt-4 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mt-4 px-4 py-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800">
          {success}
        </div>
      ) : null}

      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <Button
          type="button"
          variant="primary"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (
            <>
              <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Lucide icon="Upload" className="stroke-[1.3] w-4 h-4 mr-2" />
              {hasImage ? 'Replace image' : 'Select image'}
            </>
          )}
        </Button>
        {hasImage ? (
          <Button
            type="button"
            variant="outline-danger"
            disabled={busy}
            onClick={() => void handleRemove()}
          >
            {removing ? (
              <>
                <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                Removing…
              </>
            ) : (
              <>
                <Lucide icon="Trash2" className="stroke-[1.3] w-4 h-4 mr-2" />
                Remove
              </>
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default DuitNowQrUpload;
